import PropTypes from 'prop-types';
import { StatusBadge } from './status-badge';
import { Box } from '@dextech-ai/dex-design-system';

// Working copies always read as a draft badge; missing version → neutral fallback.
function statusFor(version) {
	if (!version) return undefined;
	return version.isWorkingCopy ? 'DRAFT' : version.status;
}

// Working copy → its label; normal version → v{version}; missing → dash.
function versionDisplay(version) {
	if (!version) return '—';
	if (version.isWorkingCopy) return version.label || 'Working copy';
	return `v${version.version}`;
}

// Published date wins; working copy = unsaved; otherwise an unpublished draft.
function metaFor(version) {
	if (!version) return 'No version selected';
	if (version.publishedDate) return `Published ${version.publishedDate}`;
	if (version.isWorkingCopy) return 'Unsaved changes';
	return 'Unpublished draft';
}

function Node({ label, version }) {
	return (
		<div className="dsv-compare__node">
			<div className="dsv-compare__node-label">{label}</div>
			<div className="dsv-compare__node-version">
				{versionDisplay(version)}
				<StatusBadge status={statusFor(version)} />
			</div>
			<div className="dsv-compare__node-meta">{metaFor(version)}</div>
		</div>
	);
}

Node.propTypes = {
	label: PropTypes.string,
	version: PropTypes.shape({
		version: PropTypes.string,
		status: PropTypes.string,
		publishedDate: PropTypes.string,
		isWorkingCopy: PropTypes.bool,
		label: PropTypes.string,
	}),
};

function SummaryPills({ summary }) {
	if (!summary || !summary.hasChanges) return null;
	return (
		<Box display="flex" wrap="wrap" gap={2}>
			<span className="dsv-compare__pill dsv-compare__pill--added">+{summary.added} added</span>
			<span className="dsv-compare__pill dsv-compare__pill--modified">~{summary.modified} modified</span>
			<span className="dsv-compare__pill dsv-compare__pill--removed">−{summary.removed} removed</span>
		</Box>
	);
}

SummaryPills.propTypes = {
	summary: PropTypes.shape({
		added: PropTypes.number,
		modified: PropTypes.number,
		removed: PropTypes.number,
		hasChanges: PropTypes.bool,
	}),
};

function CompareContextHeader({ baseVersion, targetVersion, summary, elementName, compact }) {
	return (
		<div className="dsv-compare__context">
			<div className="dsv-compare__context-top">
				{!compact && (
					<Box minW="0px">
						<div className="dsv-compare__eyebrow">Schema Comparison</div>
						<div className="dsv-compare__context-title">{elementName}</div>
						<div className="dsv-compare__context-sub">Comparing two schema versions</div>
					</Box>
				)}
				<SummaryPills summary={summary} />
			</div>
			<div className="dsv-compare__transition">
				<Node label="Base" version={baseVersion} />
				<span className="dsv-compare__arrow">⟶</span>
				<Node label="Target" version={targetVersion} />
			</div>
		</div>
	);
}

CompareContextHeader.propTypes = {
	baseVersion: PropTypes.object,
	targetVersion: PropTypes.object,
	summary: PropTypes.object,
	elementName: PropTypes.string,
	compact: PropTypes.bool,
};

CompareContextHeader.defaultProps = {
	compact: false,
};

export default CompareContextHeader;
